import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

// Get all entries with pagination and filters
export const fetchEntries = createAsyncThunk(
  "entries/fetchAll",
  async ({ page = 1, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/rental-management/entries?page=${page}`;
      
      // Add filters to URL
      if (filters.reference_number) {
        url += `&reference_number=${encodeURIComponent(filters.reference_number)}`;
      }
      if (filters.desc) {
        url += `&desc=${encodeURIComponent(filters.desc)}`;
      }
      if (filters.account_id) {
        url += `&account_id=${encodeURIComponent(filters.account_id)}`;
      }
      if (filters.created_by) {
        url += `&created_by=${encodeURIComponent(filters.created_by)}`;
      }
      
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Get single entry record
export const fetchEntryRecord = createAsyncThunk(
  "entries/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/rental-management/entries/${id}`, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Add entry
export const addEntry = createAsyncThunk(
  "entries/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append basic fields
      if (formData.entry_date) data.append("entry_date", formData.entry_date);
      if (formData.desc) data.append("desc", formData.desc);
      
      // Append items array
      if (formData.items && formData.items.length > 0) {
        formData.items.forEach((item, index) => {
          if (item.account_id) data.append(`items[${index}][account_id]`, item.account_id);
          if (item.debit) data.append(`items[${index}][debit]`, item.debit);
          if (item.credit) data.append(`items[${index}][credit]`, item.credit);
          if (item.desc) data.append(`items[${index}][desc]`, item.desc);
        });
      }
      
      // Append attachments array
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file) => {
          data.append("attachments[]", file);
        });
      }

      const response = await axios.post(`${BASE_URL}/rental-management/entries`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add entry";
      return rejectWithValue(message);
    }
  }
);

// Update entry
export const updateEntry = createAsyncThunk(
  "entries/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append basic fields
      if (formData.entry_date) data.append("entry_date", formData.entry_date);
      if (formData.desc) data.append("desc", formData.desc);
      
      // Append items array
      if (formData.items && formData.items.length > 0) {
        formData.items.forEach((item, index) => {
          if (item.account_id) data.append(`items[${index}][account_id]`, item.account_id);
          if (item.debit) data.append(`items[${index}][debit]`, item.debit);
          if (item.credit) data.append(`items[${index}][credit]`, item.credit);
          if (item.desc) data.append(`items[${index}][desc]`, item.desc);
        });
      }
      
      // Append attachments array
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file) => {
          data.append("attachments[]", file);
        });
      }

      const response = await axios.post(`${BASE_URL}/rental-management/entries/${id}`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update entry";
      return rejectWithValue(message);
    }
  }
);

// Delete entry
export const deleteEntry = createAsyncThunk(
  "entries/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/rental-management/entries/${id}`, {
        headers: getHeaders(),
      });
      return { id, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete entry";
      return rejectWithValue(message);
    }
  }
);

// Fetch accounts for dropdown - using debit/credit accounts
export const fetchAccountsForDropdown = createAsyncThunk(
  "entries/fetchAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/debit-credit-accounts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Response structure: { status: true, message: "...", data: [...] }
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load accounts";
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  entries: [],
  entryRecord: null,
  pagination: null,
  isLoading: false,
  error: null,
  success: null,
  accounts: [],
};

const entriesSlice = createSlice({
  name: "entries",
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all entries
      .addCase(fetchEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entries = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch single entry
      .addCase(fetchEntryRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntryRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entryRecord = action.payload;
      })
      .addCase(fetchEntryRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add entry
      .addCase(addEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Entry added successfully";
      })
      .addCase(addEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update entry
      .addCase(updateEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Entry updated successfully";
      })
      .addCase(updateEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete entry
      .addCase(deleteEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.data?.message || "Entry deleted successfully";
        state.entries = state.entries.filter((entry) => entry.id !== action.payload.id);
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch accounts
      .addCase(fetchAccountsForDropdown.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAccountsForDropdown.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload;
      })
      .addCase(fetchAccountsForDropdown.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearState } = entriesSlice.actions;
export default entriesSlice.reducer;

